<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { formatParsedClockTime, isNilOrBlankString, parseClockTimeInput } from '@nova/core';
import { createClockTimeRules, messages } from '@satellite/helpers/customFieldValidation';

interface TimeEntryValue {
  amPm: string;
  time: string;
}

type ValidationRule = (value: string) => boolean | string;

/** Length of a complete, zero-padded clock time such as "13:45" — shorter means still being typed. */
const COMPLETE_TIME_LENGTH = 'HH:mm'.length;

const {
  modelValue = { amPm: '', time: '' },
  disabled = false,
  timezone,
  militaryTimeEnabled,
  required = false,
  hideTimezone = false,
  hideDetails = 'auto'
} = defineProps<{
  modelValue?: TimeEntryValue;
  disabled?: boolean;
  timezone: string;
  militaryTimeEnabled: boolean;
  required?: boolean;
  hideTimezone?: boolean;
  /** Set to true to keep the error state but drop the messages, e.g. when a parent owns them. */
  hideDetails?: boolean | 'auto';
}>();

const emit = defineEmits<{
  'update:model-value': [value: TimeEntryValue];
}>();

const time = ref('');
const amPm = ref('');

function isFilledRule(message: string): ValidationRule {
  return (value: string) => !isNilOrBlankString(value) || message;
}

const hasTime = computed(() => !isNilOrBlankString(time.value));

const timeRules = computed<ValidationRule[]>(() => {
  if (!required && time.value.length === 0) {
    return [];
  }
  const rangeRules = createClockTimeRules(militaryTimeEnabled);
  if (!required) {
    return rangeRules;
  }
  /** Emptiness only: `normalizeTime` turns a half-typed "6" into "06:00" on blur. */
  return [isFilledRule(messages.required()), ...rangeRules];
});

/** A time without AM/PM parses to nothing, so it is demanded whenever the time has content. */
const amPmRules = computed<ValidationRule[]>(() => {
  const isAnswerable = !militaryTimeEnabled && (required || hasTime.value);
  return isAnswerable ? [isFilledRule(messages.selectAmOrPm())] : [];
});

function onTimeInput(value: string) {
  const digitsAndColon = String(value ?? '').replace(/[^\d:]/gu, '');
  const colonIndex = digitsAndColon.indexOf(':');

  if (colonIndex === -1) {
    time.value = digitsAndColon.slice(0, 4);
    return;
  }

  const hour = digitsAndColon.slice(0, colonIndex).replace(/:/gu, '').slice(0, 2);
  const minute = digitsAndColon
    .slice(colonIndex + 1)
    .replace(/:/gu, '')
    .slice(0, 2);
  time.value = `${hour}:${minute}`;
}

function normalizeTime() {
  const parsed = parseClockTimeInput(time.value, militaryTimeEnabled);
  if (parsed) {
    time.value = formatParsedClockTime(parsed);
  }
}

function updateVal() {
  if (time.value.length === COMPLETE_TIME_LENGTH && militaryTimeEnabled) {
    const parsed = DateTime.fromFormat(time.value, 'HH:mm');
    /**
     * Luxon stringifies an unparseable DateTime as the literal "Invalid DateTime", so an
     * unguarded toFormat here writes that text into amPm and emits it to the parent as if it
     * were a real value.
     */
    if (parsed.isValid) {
      amPm.value = parsed.toFormat('a');
    }
  }

  emit('update:model-value', {
    amPm: amPm.value,
    time: time.value
  });
}

/**
 * Only a 12-hour value carries an amPm, and only that needs converting to the 24-hour display.
 * In military mode the parent already hands over 24-hour time with an empty amPm: parsing that
 * as 'hh:mm a' produced an invalid DateTime whose "Invalid DateTime" string was then written
 * straight back to the parent by updateVal, discarding a valid stored timestamp without the
 * user touching anything.
 */
function updateLocalTime() {
  if (militaryTimeEnabled && modelValue.time.length === COMPLETE_TIME_LENGTH && modelValue.amPm) {
    const converted = DateTime.fromFormat(`${modelValue.time} ${modelValue.amPm}`, 'hh:mm a');
    if (converted.isValid) {
      time.value = converted.toFormat('HH:mm');
      amPm.value = converted.toFormat('a');
      return;
    }
  }

  time.value = modelValue.time;
  amPm.value = modelValue.amPm;
}

onBeforeMount(updateLocalTime);

watch(() => modelValue, updateLocalTime, { deep: true });
watch(amPm, updateVal);
watch(time, updateVal);
</script>

<template>
  <div class="d-flex flex-column gap-1">
    <div class="d-flex flex-row ga-2 align-start time-entry">
      <v-text-field
        v-bind="$props"
        color="primary"
        density="comfortable"
        inputmode="numeric"
        validate-on="blur"
        variant="outlined"
        width="75"
        :class="{ 'no-timezone': hideTimezone }"
        :hide-details="hideDetails"
        :data-testid="`${$attrs['test-id']}-input`"
        :model-value="time"
        :placeholder="militaryTimeEnabled ? '13:00' : '12:00'"
        :rules="timeRules"
        @blur="normalizeTime"
        @update:model-value="onTimeInput"></v-text-field>

      <!-- v-btn-toggle takes no rules and never registers with a v-form; the v-input does. -->
      <v-input
        v-if="!militaryTimeEnabled"
        #default="{ isValid }"
        :hide-details="hideDetails"
        :model-value="amPm"
        :rules="amPmRules">
        <v-btn-toggle
          divided
          mandatory
          color="primary"
          variant="outlined"
          :class="{ 'am-pm--error': isValid.value === false }"
          :disabled="disabled"
          v-model="amPm">
          <v-btn value="am" class="auto-width" :data-testid="`${$attrs['test-id']}-am-button`">
            AM
          </v-btn>
          <v-btn value="pm" class="auto-width" :data-testid="`${$attrs['test-id']}-pm-button`">
            PM
          </v-btn>
        </v-btn-toggle>
      </v-input>
    </div>

    <small class="typography-caption" v-if="!hideTimezone">
      {{ timezone }}
    </small>
  </div>
</template>

<style scoped lang="scss">
.no-timezone {
  width: 75px;
}

/* Outlined buttons draw their border from currentColor, so this reddens border and label. */
.am-pm--error .v-btn {
  color: rgb(var(--v-theme-error));
}

:deep(.v-input) {
  margin-top: 0 !important;
  padding-top: 0 !important ;
}
</style>
